import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/lib/database.types';

type Transaction = Database['public']['Tables']['transactions']['Row'] & {
  category?: Database['public']['Tables']['categories']['Row'] | null;
  account?: Database['public']['Tables']['accounts']['Row'] | null;
  to_account?: Database['public']['Tables']['accounts']['Row'] | null;
};

type TransactionInsert = Omit<Database['public']['Tables']['transactions']['Insert'], 'user_id'>;

export type TransactionType = 'income' | 'expense' | 'transfer';

export function useTransactions() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const notifyAccountsChanged = () => {
    window.dispatchEvent(new CustomEvent('accounts-need-refresh'));
  };

  const notifyAchievements = () => {
    window.dispatchEvent(new CustomEvent('achievements-need-refresh'));
  };

  /** Id of the user the list was last loaded for; null before any load. */
  const hasLoadedFor = useRef<string | null>(null);

  const loadTransactions = useCallback(async () => {
    if (!user) return;

    // Same rule as useAccounts: only the first load blanks the list. A refresh
    // that flips `loading` swaps the whole list for a spinner and back, which
    // shows up as the section flashing on every background reload.
    if (hasLoadedFor.current !== user.id) {
      setLoading(true);
    }
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('transactions')
        .select(
          `
          *,
          category:categories(*),
          account:accounts!transactions_account_id_fkey(*),
          to_account:accounts!transactions_to_account_id_fkey(*)
        `
        )
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (fetchError) throw fetchError;

      setTransactions(data || []);
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      hasLoadedFor.current = user.id;
      setLoading(false);
    }
  }, [user]);

  const createTransaction = useCallback(
    async (data: TransactionInsert) => {
      if (!user) return { success: false, error: 'Not authenticated' };

      try {
        const { error: insertError } = await supabase
          .from('transactions')
          .insert([{ ...data, user_id: user.id }]);

        if (insertError) throw insertError;

        await loadTransactions();
        notifyAccountsChanged();
        notifyAchievements();
        return { success: true };
      } catch (err) {
        console.error('Error creating transaction:', err);
        return { success: false, error: err };
      }
    },
    [user, loadTransactions]
  );

  const updateTransaction = useCallback(
    async (id: string, data: Partial<TransactionInsert>) => {
      try {
        const { error: updateError } = await supabase
          .from('transactions')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('id', id);

        if (updateError) throw updateError;

        await loadTransactions();
        notifyAccountsChanged();
        return { success: true };
      } catch (err) {
        console.error('Error updating transaction:', err);
        return { success: false, error: err };
      }
    },
    [loadTransactions]
  );

  const deleteTransaction = useCallback(
    async (id: string) => {
      try {
        const { error: deleteError } = await supabase.from('transactions').delete().eq('id', id);

        if (deleteError) throw deleteError;

        await loadTransactions();
        notifyAccountsChanged();
        notifyAchievements();
        return { success: true };
      } catch (err) {
        console.error('Error deleting transaction:', err);
        return { success: false, error: err };
      }
    },
    [loadTransactions]
  );

  const calculateStats = useCallback(() => {
    const totalIncome = transactions
      .filter((t) => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);

    const totalExpense = transactions
      .filter((t) => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);

    const totalTransfers = transactions
      .filter((t) => t.type === 'transfer')
      .reduce((sum, t) => sum + t.amount, 0);

    const balance = totalIncome - totalExpense;

    return { totalIncome, totalExpense, totalTransfers, balance };
  }, [transactions]);

  const getTransactionsByAccount = useCallback(
    (accountId: string) => {
      return transactions.filter(
        (t) => t.account_id === accountId || t.to_account_id === accountId
      );
    },
    [transactions]
  );

  const getRecentTransfers = useCallback(
    (limit: number = 5) => {
      return transactions.filter((t) => t.type === 'transfer').slice(0, limit);
    },
    [transactions]
  );

  useEffect(() => {
    if (user) {
      loadTransactions();
    }
  }, [user, loadTransactions]);

  return {
    transactions,
    loading,
    error,
    loadTransactions,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    calculateStats,
    getTransactionsByAccount,
    getRecentTransfers,
  };
}
